import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { api, unwrap } from "@/lib/api";
import { qk } from "@/lib/query-client";
import type { ApiEnvelope, SignedUrl } from "@/lib/api-types";
import { useAuth } from "@/hooks/useAuth";
import { normalizeModeId } from "@/lib/study-modes";

/**
 * UI-friendly asset types -- what each route uses internally. We map these
 * to the backend's canonical asset_type strings (underscore-separated, with
 * language suffixes for media) in `resolveBackendAssetType` below.
 */
export type AssetType =
  | "textbook"
  | "notes"
  | "question-bank"
  | "slides"
  | "podcast"
  | "video"
  | "flashcards"
  | "mind-map"
  | "quiz"
  | "summaries-manifest";

export type Language = "english" | "hindi" | "kannada";

const LANG_SUFFIX: Record<Language, string> = {
  english: "en",
  hindi: "hi",
  kannada: "kn",
};

/**
 * Convert a UI asset type (+ optional language for media) into the asset
 * type string the backend's content_handler understands. Backend whitelist:
 *   textbook, notes, slides, question_bank, mindmap, flashcards, quiz,
 *   podcast_en|hi|kn, video_en|hi|kn
 */
function resolveBackendAssetType(uiType: AssetType, language?: Language): string {
  switch (uiType) {
    case "podcast":           return `podcast_${LANG_SUFFIX[language ?? "english"]}`;
    case "video":             return `video_${LANG_SUFFIX[language ?? "english"]}`;
    // question-bank -> question_bank, mind-map -> mindmap, summaries-manifest ->
    // summaries_manifest. Shared with the progress tracker, which needs the identical
    // hyphen -> underscore fold for study-mode ids.
    default:                  return normalizeModeId(uiType);
  }
}

export type UseSignedUrlOptions = {
  chapterId: string | undefined;
  assetType: AssetType;
  /** Used for podcast / video to pick the right language variant. */
  language?: Language;
  /** Lets viewer pages wait until local access checks have resolved. */
  enabled?: boolean;
};

export type SignedUrlState = {
  url: string | null;
  expiresAt: string | null;
  /** Asset MIME type from the signed-URL response (from the DDB asset row).
   *  Lets viewers branch between legacy + new content formats, e.g. a mind
   *  map served as `image/png` (legacy) vs `application/json` (Content
   *  Factory). Null until the request resolves. */
  contentType: string | null;
  isLoading: boolean;
  isError: boolean;
  /** True when the backend returned 403 -- user lacks entitlement. */
  isLocked: boolean;
  /** True when the backend returned 404 -- the asset isn't uploaded.
   *  Useful for language switching: "Hindi podcast not yet available". */
  isNotFound: boolean;
  refresh: () => void;
};

/**
 * Resolves a short-lived presigned S3 URL for a chapter asset. Calls the
 * anonymous `/content/demo/...` endpoint when the user is signed-out, and
 * `/content/premium/...` when authenticated -- the backend signs whatever
 * the caller is entitled to. Refreshes the URL ~30s before it expires so
 * the user never sees a broken viewer mid-session.
 */
export function useSignedUrl({
  chapterId,
  assetType,
  language,
  enabled = true,
}: UseSignedUrlOptions): SignedUrlState {
  const { status } = useAuth();
  const qc = useQueryClient();

  const backendType = resolveBackendAssetType(assetType, language);

  const path =
    status === "authenticated"
      ? `/content/premium/${encodeURIComponent(chapterId ?? "")}/${backendType}`
      : `/content/demo/${encodeURIComponent(chapterId ?? "")}/${backendType}`;

  const query = useQuery({
    queryKey: [...qk.content.sign(chapterId ?? "", assetType), language ?? "default", status],
    enabled: enabled && !!chapterId && status !== "loading",
    queryFn: async () => {
      try {
        const res = await api.get<ApiEnvelope<SignedUrl>>(path);
        return unwrap(res.data);
      } catch (err) {
        if (err instanceof AxiosError) {
          if (err.response?.status === 403) throw new LockedError();
          if (err.response?.status === 404) throw new NotFoundError();
        }
        throw err;
      }
    },
    staleTime: 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof LockedError || error instanceof NotFoundError) return false;
      return failureCount < 1;
    },
  });

  useEffect(() => {
    const expiresAt = query.data?.expires_at;
    if (!expiresAt) return;
    const ms = new Date(expiresAt).getTime() - Date.now() - 30_000;
    if (ms <= 0) {
      void qc.invalidateQueries({ queryKey: qk.content.sign(chapterId ?? "", assetType) });
      return;
    }
    const handle = setTimeout(() => {
      void qc.invalidateQueries({ queryKey: qk.content.sign(chapterId ?? "", assetType) });
    }, ms);
    return () => clearTimeout(handle);
  }, [query.data?.expires_at, qc, chapterId, assetType]);

  return {
    url: query.data?.url ?? null,
    expiresAt: query.data?.expires_at ?? null,
    contentType: query.data?.content_type ?? null,
    isLoading: query.isLoading,
    isError:
      query.isError &&
      !(query.error instanceof LockedError) &&
      !(query.error instanceof NotFoundError),
    isLocked: query.error instanceof LockedError,
    isNotFound: query.error instanceof NotFoundError,
    refresh: () => void qc.invalidateQueries({ queryKey: qk.content.sign(chapterId ?? "", assetType) }),
  };
}

class LockedError extends Error {
  constructor() {
    super("Locked");
    this.name = "LockedError";
  }
}

class NotFoundError extends Error {
  constructor() {
    super("NotFound");
    this.name = "NotFoundError";
  }
}
